import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import AuthPromptContext from './AuthPromptContext';

interface WatchlistContextType {
  watchlist: string[];
  addToWatchlist: (symbol: string) => void;
  addMultipleToWatchlist: (symbols: string[]) => void;
  removeFromWatchlist: (symbol: string) => void;
  toggleWatchlist: (symbol: string) => void;
  clearWatchlist: () => void;
  replaceWatchlist: (symbols: string[]) => void;
  isInWatchlist: (symbol: string) => boolean;
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

const normalizeSymbol = (symbol: string) => symbol.trim().toUpperCase();

const getWatchlistStorageKey = (uid?: string) => `watchlist:${uid || 'guest'}`;

const areArraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((val, idx) => val === b[idx]);

export const WatchlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.uid;
  const storageKey = useMemo(() => getWatchlistStorageKey(userId), [userId]);

  const authPrompt = useContext(AuthPromptContext);
  const promptLogin = useCallback((actionDescription = 'Personal Watchlist') => {
    if (authPrompt?.openAuthPrompt) {
      authPrompt.openAuthPrompt(
        window.location.pathname + window.location.search,
        actionDescription
      );
    }
  }, [authPrompt]);

  // Read initial watchlist synchronously from localStorage for instant 0ms rendering
  const [watchlist, setWatchlist] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return Array.from(new Set(parsed.map((s) => normalizeSymbol(String(s))).filter(Boolean)));
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const watchlistRef = useRef(watchlist);
  watchlistRef.current = watchlist;

  // Persist to Firestore helper
  const syncToFirestore = useCallback(
    async (nextWatchlist: string[]) => {
      if (!db || !userId) {
        return;
      }
      try {
        const userRef = doc(db, 'users', userId);
        await setDoc(userRef, { watchlist: nextWatchlist }, { merge: true });
      } catch (err) {
        console.warn('Failed to save watchlist to Firestore:', err);
      }
    },
    [userId]
  );

  // Subscribe to real-time changes from Firestore
  useEffect(() => {
    if (!userId) {
      setWatchlist([]);
      return;
    }

    // Load from localStorage for this user if available
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const cached = Array.from(new Set(parsed.map((s) => normalizeSymbol(String(s))).filter(Boolean)));
          setWatchlist(cached);
          watchlistRef.current = cached;
        }
      }
    } catch {
      // ignore
    }

    if (!db) {
      return;
    }

    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (Array.isArray(data.watchlist)) {
            const firestoreWatchlist = Array.from(
              new Set(data.watchlist.map((s: any) => normalizeSymbol(String(s))).filter(Boolean))
            );
            if (!areArraysEqual(watchlistRef.current, firestoreWatchlist)) {
              setWatchlist(firestoreWatchlist);
              try {
                localStorage.setItem(storageKey, JSON.stringify(firestoreWatchlist));
              } catch {
                // ignore
              }
            }
          } else if (watchlistRef.current.length > 0) {
            // Automatically migrate existing browser watchlist into Firestore!
            void setDoc(userRef, { watchlist: watchlistRef.current }, { merge: true });
          }
        }
      },
      (error) => {
        console.warn('Firestore watchlist onSnapshot warning:', error);
      }
    );

    return () => unsubscribe();
  }, [storageKey, userId]);

  // Keep localStorage in sync
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(watchlist));
    } catch {
      // ignore
    }
  }, [storageKey, watchlist]);

  const addToWatchlist = useCallback(
    (symbol: string) => {
      if (!userId || !user) {
        promptLogin('Personal Watchlist');
        return;
      }
      const normalizedSymbol = normalizeSymbol(symbol);
      if (!normalizedSymbol) {
        return;
      }

      setWatchlist((current) => {
        if (current.includes(normalizedSymbol)) {
          return current;
        }
        const next = [...current, normalizedSymbol];
        void syncToFirestore(next);
        return next;
      });
    },
    [userId, user, promptLogin, syncToFirestore]
  );

  const addMultipleToWatchlist = useCallback(
    (symbols: string[]) => {
      if (!userId || !user) {
        promptLogin('Personal Watchlist');
        return;
      }
      const normalizedSymbols = (symbols ?? [])
        .map((s) => normalizeSymbol(String(s)))
        .filter(Boolean);
      if (normalizedSymbols.length === 0) {
        return;
      }

      setWatchlist((current) => {
        const set = new Set(current);
        let addedCount = 0;
        normalizedSymbols.forEach((s) => {
          if (!set.has(s)) {
            set.add(s);
            addedCount++;
          }
        });
        if (addedCount === 0) {
          return current;
        }
        const next = Array.from(set);
        void syncToFirestore(next);
        return next;
      });
    },
    [userId, user, promptLogin, syncToFirestore]
  );

  const removeFromWatchlist = useCallback(
    (symbol: string) => {
      const normalizedSymbol = normalizeSymbol(symbol);
      setWatchlist((current) => {
        const next = current.filter((item) => item !== normalizedSymbol);
        if (next.length !== current.length) {
          void syncToFirestore(next);
        }
        return next;
      });
    },
    [syncToFirestore]
  );

  const toggleWatchlist = useCallback(
    (symbol: string) => {
      if (!userId || !user) {
        promptLogin('Personal Watchlist');
        return;
      }
      const normalizedSymbol = normalizeSymbol(symbol);
      if (!normalizedSymbol) {
        return;
      }

      setWatchlist((current) => {
        const next = current.includes(normalizedSymbol)
          ? current.filter((item) => item !== normalizedSymbol)
          : [...current, normalizedSymbol];
        void syncToFirestore(next);
        return next;
      });
    },
    [userId, user, promptLogin, syncToFirestore]
  );

  const clearWatchlist = useCallback(() => {
    setWatchlist([]);
    void syncToFirestore([]);
  }, [syncToFirestore]);

  const replaceWatchlist = useCallback(
    (symbols: string[]) => {
      if (!userId || !user) {
        promptLogin('Personal Watchlist');
        return;
      }
      const normalizedWatchlist = Array.from(
        new Set((symbols ?? []).map((item) => normalizeSymbol(String(item))).filter(Boolean))
      );

      setWatchlist(normalizedWatchlist);
      void syncToFirestore(normalizedWatchlist);
    },
    [userId, user, promptLogin, syncToFirestore]
  );

  const isInWatchlist = useCallback(
    (symbol: string) => watchlist.includes(normalizeSymbol(symbol)),
    [watchlist]
  );

  return (
    <WatchlistContext.Provider
      value={{
        watchlist,
        addToWatchlist,
        addMultipleToWatchlist,
        removeFromWatchlist,
        toggleWatchlist,
        clearWatchlist,
        replaceWatchlist,
        isInWatchlist,
      }}
    >
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within a WatchlistProvider');
  }

  return context;
};
